import { UploadedFile, UploadingFile } from '@/ui-kit/upload-file/types';
import { useEffect, useReducer, useState } from 'react';

type FileState = {
  uploading: Record<string, UploadingFile>;
  uploaded: Record<string, UploadedFile>;
};

type Action =
  | { type: 'UPLOADING'; file: UploadingFile }
  | { type: 'UPLOADED'; file: UploadingFile }
  | { type: 'REMOVE'; fileName: string };

const fileReducer = (state: FileState, action: Action): FileState => {
  switch (action.type) {
    case 'UPLOADING':
      return {
        uploaded: { ...state.uploaded },
        uploading: { ...state.uploading, [action.file.name]: action.file },
      };
    case 'UPLOADED': {
      const newUploading = { ...state.uploading };
      delete newUploading[action.file.name];
      return {
        uploaded: {
          ...state.uploaded,
          [action.file.name]: {
            name: action.file.name,
            preview: action.file.preview,
            realName: action.file.realName,
          },
        },
        uploading: newUploading,
      };
    }
    case 'REMOVE': {
      const newUploaded = { ...state.uploaded };
      const newUploading = { ...state.uploading };
      delete newUploaded[action.fileName];
      delete newUploading[action.fileName];
      return {
        uploaded: newUploaded,
        uploading: newUploading,
      };
    }
    default:
      return state;
  }
};

export const useFileUpload = () => {
  const [fileList, setFileList] = useState<Record<string, UploadingFile>>({});

  const [files, dispatch] = useReducer(fileReducer, {
    uploading: {},
    uploaded: {},
  });

  useEffect(() => {
    const fileArray = Object.values(fileList);

    const dispatchType = fileArray.every(file => file.progress === 100)
      ? 'UPLOADED'
      : 'UPLOADING';

    fileArray.forEach(file => {
      dispatch({ type: dispatchType, file });
    });
  }, [fileList]);

  const clearFiles = () => {
    setFileList({});
  };

  const setFile = (file: UploadingFile) => {
    setFileList(prevState => ({
      ...prevState,
      [file.name]: file,
    }));
  };

  const updateFile = (file: UploadingFile) => {
    setFileList(prevState => {
      const newState = { ...prevState[file.name] };

      return {
        ...prevState,
        [file.name]: {
          ...newState,
          ...file,
        },
      };
    });
  };

  const removeFile = (fileName: string) => {
    dispatch({ type: 'REMOVE', fileName });

    setFileList(prevState => {
      const newState = { ...prevState };
      delete newState[fileName];
      return newState;
    });
  };

  const removeAll = () => {
    Object.keys(fileList).forEach(fileName => {
      dispatch({ type: 'REMOVE', fileName });
    });
    clearFiles();
  };

  return {
    files,
    clearFiles,
    setFile,
    updateFile,
    removeFile,
    removeAll,
  };
};
